import { Button } from '@/app/common/components/button/Button'
import { Input } from '@/app/common/components/input/Input'
import Label from '@/app/common/components/Label'
import { currencyFormatter } from '@/lib/utils'
import { AlertTriangle } from 'lucide-react'
import type { Purse } from '@/queries/program_purses/schemas.ts'
import { StudentAwardInformation } from '../award-form'
import Badge from '@/app/common/components/Badge'
import FormButtons from '../_components/form-buttons'

interface ConfirmAwardStepProps {
  selectedPurse: Purse
  selectedStudents: StudentAwardInformation[]
  confirmationText: string
  onConfirmationTextChange: (text: string) => void
  isConfirmationValid: boolean
  onConfirmAward: () => void
  isSubmitting?: boolean
  onBackClick: () => void
}

export default function ConfirmAwardStep({
  selectedPurse,
  selectedStudents,
  confirmationText,
  onConfirmationTextChange,
  isConfirmationValid,
  onConfirmAward,
  isSubmitting = false,
  onBackClick,
}: ConfirmAwardStepProps) {
  return (
    <div className="space-y-6">
      {/* Warning Banner */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm">
            <p className="font-medium text-amber-800 mb-1">
              Final Confirmation Required
            </p>
            <p className="text-amber-700">
              You are about to award scholarships to {selectedStudents.length}{' '}
              student {selectedStudents.length !== 1 ? 's' : ''}
              from the "{selectedPurse?.name}" purse. This action is
              irreversible.
            </p>
          </div>
        </div>
      </div>

      {/* Award Summary */}
      <div className="space-y-4">
        <div>
          <div className="mb-2">
            <Label>Funding Source</Label>
          </div>
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="flex justify-between items-center">
              <span className="font-medium">{selectedPurse?.name}</span>
              <span className="text-sm text-gray-600">
                Available:{' '}
                {currencyFormatter.format(
                  selectedPurse?.balance_available || 0
                )}
              </span>
            </div>
          </div>
        </div>

        <div>
          <div className="mb-2">
            <Label>Awarded Students ({selectedStudents.length})</Label>
          </div>
          <div className="bg-gray-50 rounded-lg p-3 max-h-40 overflow-y-auto">
            <div className="space-y-2">
              {selectedStudents.map((student) => (
                <div
                  key={student.id}
                  className="flex items-center justify-between gap-2 text-sm"
                >
                  <div className="flex items-center gap-2">
                    <div className="h-6 w-6 bg-gray-200 rounded-full inline-flex items-center justify-center">
                      <div className="text-xs text-gray-600 font-medium">
                        {student.first_name?.[0]?.toUpperCase()}
                        {student.last_name?.[0]?.toUpperCase()}
                      </div>
                    </div>
                    <span>
                      {student.first_name} {student.last_name}{' '}
                      <span className="text-gray-500">({student.id})</span>
                    </span>
                  </div>
                  <div className="flex gap-2">
                    {student.isManuallyAwarded && (
                      <Badge semanticColor="Informative">
                        Manually Awarded
                      </Badge>
                    )}
                    {student.is_recieving_additional_award && (
                      <Badge semanticColor="Negative">Additional Award</Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Confirmation Input */}
        <div>
          <Label>
            Type the purse name "
            <span className="font-mono font-medium">{selectedPurse?.name}</span>
            " to confirm
          </Label>
          <Input
            type="text"
            value={confirmationText}
            onChange={(e) => onConfirmationTextChange(e.target.value)}
            placeholder={`Type "${selectedPurse?.name}" to confirm`}
            className="mt-1"
            autoComplete="off"
          />
          {confirmationText && !isConfirmationValid && (
            <p className="text-sm text-red-600 mt-1">
              Purse name does not match. Please type exactly:{' '}
              {selectedPurse?.name}
            </p>
          )}
        </div>

        {/* Confirm Button */}
        <div className="flex justify-end pt-4">
          <FormButtons>
            <FormButtons.BackButton onClick={onBackClick} />
            <Button
              onClick={onConfirmAward}
              disabled={!isConfirmationValid || isSubmitting}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {isSubmitting ? 'Awarding...' : 'Confirm Award'}
            </Button>
          </FormButtons>
        </div>
      </div>
    </div>
  )
}
